import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../context/ThemeContext";

const NAV_LINKS = [
  { path: "/", label: "Home" },
  { path: "/chat", label: "Chat" },
  { path: "/vocabulary", label: "Glossary" },
  { path: "/built-by", label: "Built by" },
];

// ── Sun / Moon toggle icon ─────────────────────────────────────────────────
function ThemeToggle({ isDark, setIsDark }) {
  return (
    <button
      className="btn btn-ghost theme-toggle"
      onClick={() => setIsDark(!isDark)}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Light mode" : "Dark mode"}
    >
      {isDark ? "☀" : "☾"}
    </button>
  );
}

export default function Navbar() {
  const { isDark, setIsDark } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav className={`navbar ${scrolled ? "navbar--scrolled" : ""}`}>
      <div className="navbar__inner container">
        {/* Logo */}
        <Link to="/" className="navbar__logo">
          <span className="navbar__logo-icon">✦</span>
          <span className="navbar__logo-text">
            Arcane<span className="text-accent"> Illumina</span>
          </span>
        </Link>

        {/* Desktop links */}
        <ul className="navbar__links">
          {NAV_LINKS.map(({ path, label }) => (
            <li key={path}>
              <NavLink
                to={path}
                end={path === "/"}
                className={({ isActive }) =>
                  `navbar__link ${isActive ? "navbar__link--active" : ""}`
                }
              >
                {label}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Right controls */}
        <div className="navbar__controls">
          <ThemeToggle isDark={isDark} setIsDark={setIsDark} />
          <Link to="/chat" className="btn btn-primary navbar__cta">
            Ask AI
          </Link>

          {/* Hamburger (mobile) */}
          <button
            className="navbar__hamburger btn btn-ghost"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? "✕" : "☰"}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="navbar__mobile"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {NAV_LINKS.map(({ path, label }) => (
              <NavLink
                key={path}
                to={path}
                end={path === "/"}
                className={({ isActive }) =>
                  `navbar__mobile-link ${isActive ? "navbar__mobile-link--active" : ""}`
                }
              >
                {label}
              </NavLink>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          height: var(--nav-height);
          background: transparent;
          transition: background var(--transition-base), backdrop-filter var(--transition-base),
                      box-shadow var(--transition-base);
        }
        .navbar--scrolled {
          background: var(--nav-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          box-shadow: 0 1px 0 var(--border);
        }
        .navbar__inner {
          display: flex;
          align-items: center;
          height: 100%;
          gap: var(--space-xl);
        }
        .navbar__logo {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          font-family: var(--font-heading);
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text);
          text-decoration: none;
          flex-shrink: 0;
        }
        .navbar__logo-icon { color: var(--accent); font-size: 1.2rem; }
        .navbar__links {
          display: flex;
          list-style: none;
          gap: var(--space-sm);
          margin-left: var(--space-lg);
        }
        .navbar__link {
          color: var(--text-muted);
          font-size: 0.9rem;
          font-weight: 500;
          padding: var(--space-xs) var(--space-md);
          border-radius: var(--radius-full);
          transition: color var(--transition-fast), background var(--transition-fast);
        }
        .navbar__link:hover { color: var(--text); background: var(--surface-hover); opacity: 1; }
        .navbar__link--active { color: var(--accent); }
        .navbar__controls {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          margin-left: auto;
        }
        .theme-toggle { font-size: 1.1rem; }
        .navbar__cta { font-size: 0.85rem; padding: 0.45rem 1.1rem; }
        .navbar__hamburger { display: none; font-size: 1.2rem; }
        .navbar__mobile {
          position: absolute;
          top: var(--nav-height);
          left: 0;
          right: 0;
          background: var(--nav-bg);
          backdrop-filter: blur(16px);
          padding: var(--space-md);
          display: flex;
          flex-direction: column;
          gap: var(--space-xs);
          border-bottom: 1px solid var(--border);
        }
        .navbar__mobile-link {
          color: var(--text-muted);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          font-size: 0.95rem;
        }
        .navbar__mobile-link:hover { background: var(--surface-hover); color: var(--text); opacity: 1; }
        .navbar__mobile-link--active { color: var(--accent); }
        @media (max-width: 768px) {
          .navbar__links { display: none; }
          .navbar__cta  { display: none; }
          .navbar__hamburger { display: flex; }
        }
      `}</style>
    </nav>
  );
}
